import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { oneKlicks } from '../../../SDK/oneclick';
import decodeHtml from '../../../Uitiles/decodeHtml';
import './CheckoutPage.css';

const CheckoutPage = () => {
    const { doctorId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [doctorDetails, setDoctorDetails] = useState(null);
    const [bookingData, setBookingData] = useState({
        date: '',
        timeSlot: '',
        name: '',
        phone: '',
        reason: ''
    });

    // Razorpay test keys
    const RAZORPAY_KEY_ID = 'rzp_live_SxrHxAcaqcutXT';
    const RAZORPAY_KEY_SECRET = 'U0PKiM2h6J6S86ZCMQ82WdFG';

    // Get today's date in YYYY-MM-DD format for min date
    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        // Get booking data from URL params or localStorage
        const urlParams = new URLSearchParams(location.search);
        const storedBooking = localStorage.getItem('tempBookingData');

        if (storedBooking) {
            setBookingData(JSON.parse(storedBooking));
        } else if (urlParams.get('date')) {
            setBookingData({
                date: urlParams.get('date') || '',
                timeSlot: urlParams.get('timeSlot') || '',
                name: urlParams.get('name') || '',
                phone: urlParams.get('phone') || '',
                reason: urlParams.get('reason') || ''
            });
        }

        // Fetch doctor details
        if (doctorId) {
            fetchDoctorDetails();
        }
    }, [doctorId, location]);

    const fetchDoctorDetails = async () => {
        try {
            const tableSchema = '';
            const tableName = 'users';
            const conditions = { id: doctorId };
            const orderBy = '';
            const search = '';
            const pagination = '';
            const margedata = [
                {
                    target_table: 'doctor_default_location',
                    target_column: 'user_id',
                    target_value: 'id',
                    target_label: 'doctor_default_location'
                },
                {
                    target_table: 'doctor_specialities',
                    target_column: 'doctor_id',
                    target_value: 'id',
                    target_label: 'doctor_specialities',
                    margedata: [
                        {
                            target_table: 'specialities',
                            target_column: 'id',
                            target_value: 'speciality_id',
                            target_label: 'specialities'
                        }
                    ]
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            if (response && response.data && response.data.length > 0) {
                setDoctorDetails(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching doctor details:', error);
        }
    };

    const formatDoctorName = (name) => {
        if (!name) return '';
        let formattedName = name.replace(/^dr_/, '');
        formattedName = formattedName.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        formattedName = formattedName.replace(/^(Dr\.?\s*)+/i, '');
        return `Dr. ${formattedName}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBookingData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const showToast = (message, type = 'success') => {
        const toast = document.createElement('div');
        toast.className = `toast ${type}`;
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${type === 'success' ? '#4CAF50' : '#f44336'};
            color: white;
            padding: 16px 24px;
            border-radius: 4px;
            z-index: 10000;
            font-weight: 500;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 3000);
    };

    const initializeRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve();
            script.onerror = () => resolve();
            document.body.appendChild(script);
        });
    };

    const handlePayment = async () => {
        // Validate form
        if (!bookingData.name || !bookingData.phone || !bookingData.date || !bookingData.timeSlot) {
            showToast('Please fill in all required fields', 'error');
            return;
        }

        setLoading(true);

        try {
            await initializeRazorpay();

            const consultationFee = Number(doctorDetails?.starting_price) || 500;
            const totalAmount = consultationFee; // Patient pays only consultation fee

            const options = {
                key: RAZORPAY_KEY_ID,
                amount: totalAmount * 100, // Amount in paise
                currency: 'INR',
                name: 'MeddyList',
                description: `Appointment with ${formatDoctorName(doctorDetails?.display_name)}`,
                image: 'https://tech2day.co.in/static/media/logo.10514b6e5cb4bdd5352f.png',
                handler: function (response) {
                    console.log('Payment successful:', response);

                    if (response.razorpay_payment_id) {
                        showToast('Payment successful! Booking confirmed.', 'success');

                        // Save booking to database
                        saveBookingToDatabase(response);

                        // Clear temporary data
                        localStorage.removeItem('tempBookingData');

                        // Redirect to home page after successful payment
                        setTimeout(() => {
                            navigate('/?booking=success');
                        }, 2000);
                    } else {
                        showToast('Payment verification failed. Please contact support.', 'error');
                    }
                },
                prefill: {
                    name: bookingData.name,
                    contact: bookingData.phone,
                    email: 'test@example.com'
                },
                notes: {
                    doctor_id: doctorId,
                    appointment_date: bookingData.date,
                    appointment_time: bookingData.timeSlot
                },
                theme: {
                    color: '#007bff'
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                        showToast('Payment cancelled by user.', 'error');
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            rzp.on('payment.failed', function (response) {
                console.error('Payment failed:', response.error);
                showToast(`Payment failed: ${response.error.description || 'Unknown error'}`, 'error');
                setLoading(false);
            });

            rzp.on('payment.error', function (response) {
                console.error('Payment error:', response.error);
                showToast(`Payment error: ${response.error.description || 'Unknown error'}`, 'error');
                setLoading(false);
            });

            rzp.open();

        } catch (error) {
            console.error('Payment error:', error);
            showToast('Payment initialization failed. Please try again.', 'error');
            setLoading(false);
        }
    };

    const saveBookingToDatabase = async (paymentResponse) => {
        try {
            const consultationFee = Number(doctorDetails?.starting_price) || 500;
            const platformFee = Math.round(consultationFee * 0.05); // MeddyList keeps 5%
            const totalAmount = consultationFee; // Patient paid amount

            const tableSchema = '';
            const tableName = 'appointments';

            const bookingRecord = {
                patient_id: userData?.id || null,
                doctor_id: doctorId,
                hospital_id: doctorDetails.hospital_id || null,
                service_id: doctorDetails.doctor_specialities?.[0]?.speciality_id || null,
                appointment_date: bookingData.date,
                appointment_time: bookingData.timeSlot,
                note: bookingData.reason,
                status: 'confirmed',
                payment_mode: 'online',
                payment_status: 'completed',
                amount: totalAmount.toString(),
                base_amount: consultationFee.toString(),
                transaction_fee: platformFee.toString(),
                display_currency: 'INR',
                display_amount: totalAmount.toString(),
                // Keep these for legacy support/extra details
                doctor_name: formatDoctorName(doctorDetails?.display_name),
                patient_name: bookingData.name,
                patient_phone: bookingData.phone,
                payment_id: paymentResponse.razorpay_payment_id,
                created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            const conditions = {};

            const response = await oneKlicks.create(tableSchema, tableName, bookingRecord, conditions);
            console.log('Booking saved to database:', response);

        } catch (error) {
            console.error('Error saving booking to database:', error);
        }
    };

    const handleBackToDoctor = () => {
        navigate(`/doctor/${doctorDetails?.display_name}/${doctorId}`);
    };

    if (!doctorDetails) {
        return (
            <div className="checkout-loading">
                <div className="loading-spinner"></div>
                <p>Loading doctor details...</p>
            </div>
        );
    }

    const consultationFee = Number(doctorDetails?.starting_price) || 500;
    const totalAmount = consultationFee; // Patient pays only consultation fee

    return (
        <div className="checkout-page">
            <div className="checkout-container">
                <div className="checkout-header">
                    <button onClick={handleBackToDoctor} className="back-btn">
                        â† Back to Doctor
                    </button>
                    <h1>Checkout</h1>
                </div>

                <div className="checkout-content">
                    <div className="checkout-left">
                        <div className="doctor-card">
                            <div className="doctor-info">
                                <img
                                    src={doctorDetails.profile_url ?
                                        (process.env.REACT_APP_API_BASE_URL + doctorDetails.profile_url) :
                                        "https://apis.oneklicks.com/uploads/3/doctors/1746963970092_default-placeholder-doctor-halflength-portrait-600nw-1058724875.png"
                                    }
                                    alt={doctorDetails.display_name}
                                    className="doctor-image"
                                />
                                <div className="doctor-details">
                                    <h2>{formatDoctorName(doctorDetails.display_name)}</h2>
                                    <p className="doctor-speciality">
                                        {doctorDetails.doctor_specialities?.[0]?.specialities?.name || 'General Physician'}
                                    </p>
                                    <p className="doctor-location">
                                        {doctorDetails.doctor_default_location?.[0]?.address || doctorDetails.address}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="booking-form">
                            <h3>Appointment Details</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Appointment Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={bookingData.date}
                                        onChange={handleInputChange}
                                        min={today}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Appointment Time</label>
                                    <select
                                        name="timeSlot"
                                        value={bookingData.timeSlot}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select time</option>
                                        <option value="09:00 AM">09:00 AM</option>
                                        <option value="09:30 AM">09:30 AM</option>
                                        <option value="10:00 AM">10:00 AM</option>
                                        <option value="10:30 AM">10:30 AM</option>
                                        <option value="11:00 AM">11:00 AM</option>
                                        <option value="11:30 AM">11:30 AM</option>
                                        <option value="12:00 PM">12:00 PM</option>
                                        <option value="12:30 PM">12:30 PM</option>
                                        <option value="02:00 PM">02:00 PM</option>
                                        <option value="02:30 PM">02:30 PM</option>
                                        <option value="03:00 PM">03:00 PM</option>
                                        <option value="03:30 PM">03:30 PM</option>
                                        <option value="04:00 PM">04:00 PM</option>
                                        <option value="04:30 PM">04:30 PM</option>
                                        <option value="05:00 PM">05:00 PM</option>
                                        <option value="05:30 PM">05:30 PM</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Your Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={bookingData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={bookingData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Enter your phone number"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Reason for Visit *</label>
                                <textarea
                                    name="reason"
                                    value={bookingData.reason}
                                    onChange={handleInputChange}
                                    placeholder="Describe your symptoms or reason for visit"
                                    rows="4"

                                />
                            </div>
                        </div>
                    </div>

                    <div className="checkout-right">
                        <div className="payment-summary">
                            <h3>Payment Summary</h3>

                            <div className="appointment-details">
                                <h4>Appointment Details</h4>
                                <div className="detail-item">
                                    <span>Date:</span>
                                    <span>{bookingData.date || 'Not selected'}</span>
                                </div>
                                <div className="detail-item">
                                    <span>Time:</span>
                                    <span>{bookingData.timeSlot || 'Not selected'}</span>
                                </div>
                            </div>

                            <div className="fee-breakdown">
                                <h4>Fee Breakdown</h4>
                                <div className="fee-item total">
                                    <span>Consultation Fee:</span>
                                    <span>₹{totalAmount}</span>
                                </div>
                            </div>

                            <div className="platform-note">
                                <p>ðŸ’¡ MeddyList handles payment processing and doctor coordination.</p>
                            </div>

                            <button
                                onClick={handlePayment}
                                className="pay-button"
                                disabled={loading}
                            >
                                {loading ? 'Processing Payment...' : `Pay ₹${totalAmount}`}
                            </button>

                            <div className="secure-payment">
                                <p>🔒 Secure payment powered by Razorpay</p>
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                    ðŸ’³ Test Mode - Use any card number for testing
                                </p>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#888',
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <strong>Test Cards:</strong><br />
                                    â€¢ 4111 1111 1111 1111 (Visa)<br />
                                    â€¢ 5555 5555 5555 4444 (Mastercard)<br />
                                    â€¢ Any future date & CVV
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
